import { parseScript, type ParsedScript } from "@cuekards/aligner";

/**
 * The overlay's display model. `parseScript` gives the spoken word index and the
 * director notes with their source offsets; this reassembles them into the lines
 * the overlay actually draws, tagging each word token with the span of
 * spoken-word indices it covers so karaoke highlighting (FR-25) and manual
 * line-stepping (ticket 07) can both address it.
 *
 * Pure and free of Node and Electron, so the main process (which computes
 * nudge targets) and the renderer (which draws) build the identical model from
 * the same source text — one source of truth, no round-trip.
 */

/** A run of spoken text shown as one token — usually a single word, but a
 * numeral expands to several spoken words behind one on-screen token (FR-15). */
export interface DisplayWord {
  kind: "word";
  /** The original source slice, shown verbatim (e.g. "don't", "2024"). */
  text: string;
  /** First spoken-word index this token covers. */
  firstWordIndex: number;
  /** Last spoken-word index this token covers (equal to `firstWordIndex`
   * unless a numeral expanded to several spoken words). */
  lastWordIndex: number;
}

/** A director note, rendered dimmed and smaller and never part of a word span
 * (FR-29). */
export interface DisplayNote {
  kind: "note";
  text: string;
}

/**
 * The source text between two tokens, kept verbatim: punctuation, spacing, and
 * anything else that is not a spoken word or a note.
 *
 * A word's source span ends at its last letter — `WORD_RE` matches letters,
 * digits and apostrophes and nothing else — so `"Well, then."` is two words with
 * `, ` and `.` falling outside both. Those characters used to have nowhere to go:
 * the prompter rebuilt each line by joining its tokens with a single space, which
 * silently deleted every comma, full stop, dash and quotation mark in the script.
 * A Google Doc came out as an unreadable run of bare words.
 *
 * They are tokens rather than being glued onto the word before them because not
 * all of them belong to a word: a spaced em-dash between two clauses belongs to
 * neither, and the space between two words belongs to neither. Being a token also
 * means the renderer can give punctuation the same spoken/current/upcoming colour
 * as the word it follows, so a full stop dims with its sentence.
 */
export interface DisplayGap {
  kind: "gap";
  text: string;
}

export type DisplayToken = DisplayWord | DisplayNote | DisplayGap;

/** One source line. Blank and note-only lines carry null word bounds so the
 * anchor and nudge logic skip straight over them. */
export interface DisplayLine {
  tokens: DisplayToken[];
  /** First spoken-word index appearing on this line, or null if it has none. */
  firstWordIndex: number | null;
  /** Last spoken-word index appearing on this line, or null if it has none. */
  lastWordIndex: number | null;
}

export interface ScriptModel {
  lines: DisplayLine[];
  /** Total spoken words in the script (the alignment index length). */
  wordCount: number;
}

/** A one-line step: +1 forward (down the script), -1 back (up). */
export type NudgeDirection = 1 | -1;

/** The empty model, used before any script has loaded. */
export const EMPTY_MODEL: ScriptModel = { lines: [], wordCount: 0 };

/** A line carrying at least one spoken word — the only lines the anchor rests
 * on and the nudge steps between; blank and note-only lines are passed over. */
function isContentLine(line: DisplayLine): boolean {
  return line.firstWordIndex !== null;
}

interface PlacedToken {
  start: number;
  /** Source offset just past this token — where the gap after it begins. */
  end: number;
  token: DisplayToken;
}

/** Group consecutive words sharing one source token (a numeral expansion) into
 * a single display word carrying the full span of spoken indices. */
function groupWords(parsed: ParsedScript): PlacedToken[] {
  const out: PlacedToken[] = [];
  const words = parsed.words;
  let i = 0;
  while (i < words.length) {
    const first = words[i]!;
    let j = i + 1;
    while (
      j < words.length &&
      words[j]!.sourceStart === first.sourceStart &&
      words[j]!.sourceEnd === first.sourceEnd
    ) {
      j++;
    }
    out.push({
      start: first.sourceStart,
      end: first.sourceEnd,
      token: {
        kind: "word",
        text: parsed.source.slice(first.sourceStart, first.sourceEnd),
        firstWordIndex: first.index,
        lastWordIndex: words[j - 1]!.index,
      },
    });
    i = j;
  }
  return out;
}

/** Start offset of each source line, in order. */
function lineStarts(source: string): number[] {
  const starts: number[] = [];
  let offset = 0;
  for (const line of source.split("\n")) {
    starts.push(offset);
    offset += line.length + 1; // +1 for the consumed "\n"
  }
  return starts;
}

export function buildScriptModel(source: string): ScriptModel {
  return buildScriptModelFromParsed(parseScript(source));
}

/**
 * Build the display model from an already-parsed script. The main process
 * parses once and keeps the {@link ParsedScript} (for refresh re-anchoring),
 * so it builds the model from that rather than paying to parse the same text
 * twice.
 */
export function buildScriptModelFromParsed(parsed: ParsedScript): ScriptModel {
  const source = parsed.source;
  const placed: PlacedToken[] = groupWords(parsed);
  for (const note of parsed.notes) {
    placed.push({
      start: note.sourceStart,
      end: note.sourceEnd,
      token: { kind: "note", text: note.text },
    });
  }
  placed.sort((a, b) => a.start - b.start);

  const starts = lineStarts(source);
  const lines: DisplayLine[] = starts.map(() => ({
    tokens: [],
    firstWordIndex: null,
    lastWordIndex: null,
  }));
  /** The placed tokens belonging to each line, before the gaps are woven in. */
  const perLine: PlacedToken[][] = starts.map(() => []);

  // Two-pointer walk: both `placed` and `starts` are ascending, so each token's
  // line is found by advancing the line cursor, never rescanning.
  let li = 0;
  for (const item of placed) {
    while (li < starts.length - 1 && item.start >= starts[li + 1]!) li++;
    perLine[li]!.push(item);
    const line = lines[li]!;
    if (item.token.kind === "word") {
      line.firstWordIndex =
        line.firstWordIndex === null
          ? item.token.firstWordIndex
          : Math.min(line.firstWordIndex, item.token.firstWordIndex);
      line.lastWordIndex =
        line.lastWordIndex === null
          ? item.token.lastWordIndex
          : Math.max(line.lastWordIndex, item.token.lastWordIndex);
    }
  }

  // Weave the source between the tokens back in, so a line renders as what the
  // script actually says rather than as its spoken words with single spaces
  // between them. A line with no tokens keeps none: an empty `tokens` is how a
  // blank line asks for a paragraph gap, and a gap token would not be one.
  for (let i = 0; i < lines.length; i++) {
    const items = perLine[i]!;
    if (items.length === 0) continue;
    const lineEnd = i + 1 < starts.length ? starts[i + 1]! - 1 : source.length;
    const tokens: DisplayToken[] = [];
    let cursor = starts[i]!;
    for (const item of items) {
      pushGap(tokens, source.slice(cursor, item.start));
      tokens.push(item.token);
      cursor = item.end;
    }
    pushGap(tokens, source.slice(cursor, lineEnd));
    lines[i]!.tokens = tokens;
  }

  return { lines, wordCount: parsed.words.length };
}

/** Append `text` as a gap, unless there is nothing there to keep. */
function pushGap(tokens: DisplayToken[], text: string): void {
  if (text.length > 0) tokens.push({ kind: "gap", text });
}

/**
 * The index of the content line that `position` sits on, or -1 when position is
 * before the first spoken word. Blank and note-only lines are never returned —
 * a position always resolves to the line that owns that spoken word.
 */
export function lineIndexForPosition(model: ScriptModel, position: number): number {
  if (position < 0) return -1;
  let result = -1;
  for (let i = 0; i < model.lines.length; i++) {
    const line = model.lines[i]!;
    if (!isContentLine(line)) continue;
    if (position < line.firstWordIndex!) break;
    result = i;
    if (position <= line.lastWordIndex!) break;
  }
  return result;
}

/**
 * The content line the overlay anchor should hold for `position`: the line
 * owning the current word, or — before the first word is reached (position < 0)
 * — the first content line. Returns -1 only when the script has no spoken words
 * at all.
 */
export function anchorLineIndex(model: ScriptModel, position: number): number {
  const line = lineIndexForPosition(model, position);
  if (line >= 0) return line;
  return model.lines.findIndex(isContentLine);
}

/**
 * The next content line after `from`, or -1 when there is none. `from` itself is
 * never returned, and blank and note-only lines are passed over — this is the
 * line the anchor is heading toward, so it has to be a line the anchor can rest
 * on.
 */
export function nextContentLine(model: ScriptModel, from: number): number {
  for (let i = from + 1; i < model.lines.length; i++) {
    if (isContentLine(model.lines[i]!)) return i;
  }
  return -1;
}

/**
 * How far the anchor should have swept toward the next content line, as a signed
 * fraction of the gap between the two: -0.5 at the anchored line's first spoken
 * word, just under +0.5 at its last, and 0 before the script has started.
 *
 * **Why a sweep at all.** The anchor used to be the current line's centre, full
 * stop, which meant every word on a line produced the identical translation and
 * the prompter did not move until the line changed — then moved a whole line at
 * once. On a Google Doc paragraph that is many words of dead zone followed by a
 * jump, which is both the reported "it lags behind what I said" and the page turn
 * FR-26 forbids, arriving by a door nobody was watching. Spreading the same
 * scroll across the words that caused it fixes both: the page moves on every word
 * and never moves far.
 *
 * **Why signed and centred.** The mean of this over a line is zero, so the *average*
 * anchor is exactly where line-granular anchoring put it — the reading line stays
 * where the user has already learned it is, and only the wobble around it is new.
 * An unsigned 0..1 sweep would have shifted every line's resting place up by half a
 * step.
 *
 * The half-word offset is what keeps a one-word line at 0 rather than at an edge,
 * and it is why the last word of a line reaches just *under* +0.5 rather than
 * landing exactly on the next line's resting place.
 */
export function anchorSweep(model: ScriptModel, position: number): number {
  if (position < 0) return 0;
  const index = anchorLineIndex(model, position);
  if (index < 0) return 0;
  const line = model.lines[index]!;
  const first = line.firstWordIndex!;
  const last = line.lastWordIndex!;
  const span = last - first + 1;
  // A position past the line's last word means the anchor has not caught up to a
  // later line yet; clamping keeps the sweep inside the gap it is a fraction of.
  const into = Math.min(Math.max(position - first, 0), span - 1);
  return (into + 0.5) / span - 0.5;
}

/**
 * Where a word stands against the reader: already said, being said, or still to
 * come. The karaoke colouring (FR-25) is these three and nothing else.
 */
export type WordState = "spoken" | "current" | "upcoming";

/**
 * The state of one word token at `position`, the word most recently spoken.
 *
 * Here rather than in a renderer since mobile-app ticket 37, when the phone gave
 * up its size gap and started colouring words the way the web does. The rule was
 * inline in `OverlayApp.tsx` until then, and a second host copying it would have
 * been a second place for "which words are said" to be decided.
 *
 * A token covers a *span* of spoken indices — a numeral expands to several words
 * behind one on-screen token (FR-15) — so it is current for as long as the
 * position is anywhere inside that span, and spoken only once it is past the end.
 */
export function wordState(token: DisplayWord, position: number): WordState {
  if (position > token.lastWordIndex) return "spoken";
  if (position >= token.firstWordIndex) return "current";
  return "upcoming";
}

/**
 * Every token on a line with the state it is drawn in, in token order: a word's
 * own {@link wordState}, a gap's taken from the word before it, and `null` for a
 * director's note, which is not the script and is never coloured as if it were.
 *
 * **Why a gap follows the word before it.** Punctuation belongs to the word it
 * ends, so a full stop dims with its sentence rather than staying lit in front of
 * the next one. A gap before the line's first word has nothing before it and is
 * upcoming, which is what every word after it is too.
 *
 * **Why the gap after the current word counts as spoken.** `current` is the one
 * state that is a highlight rather than a shade — a glow on the web, signal green
 * on the phone — and highlighted whitespace either side of the word reads as a
 * smear rather than as the word. The punctuation after the word you are on is
 * already said.
 */
export function lineTokenStates(
  line: DisplayLine,
  position: number,
): (WordState | null)[] {
  let trailing: WordState = "upcoming";
  return line.tokens.map((token) => {
    if (token.kind === "note") return null;
    if (token.kind === "gap") return trailing;
    const state = wordState(token, position);
    trailing = state === "current" ? "spoken" : state;
    return state;
  });
}

/**
 * Where `position` sits *as far as this line is concerned*: one before its first
 * word, one after its last, or the real value when it is inside. A line with no
 * words gets -1, since there is nothing on it to colour.
 *
 * This is what lets a line skip re-rendering, on both hosts. Every line's
 * colouring depends on the position only through which of its own words are
 * behind it, so a reader eighty words down the script is indistinguishable, to
 * the line at the top, from a reader forty words down — both are "past all of
 * it". Clamping makes the two cases the same value, and the same value is a prop
 * that did not change: an advance re-renders the line being read and the line
 * just left, and nothing else.
 */
export function clampToLine(line: DisplayLine, position: number): number {
  const first = line.firstWordIndex;
  const last = line.lastWordIndex;
  if (first === null || last === null) return -1;
  if (position < first) return first - 1;
  if (position > last) return last + 1;
  return position;
}

/**
 * Move the anchor one content line forward (`dir` +1) or back (`dir` -1),
 * skipping blank and note-only lines, and return the new word position — the
 * first spoken word of the target line. At either edge, or with no spoken words
 * at all, the current position is held rather than running off the end.
 */
export function nudgeLine(
  model: ScriptModel,
  position: number,
  dir: NudgeDirection,
): number {
  const current = lineIndexForPosition(model, position);
  const lines = model.lines;
  if (dir === 1) {
    for (let i = current + 1; i < lines.length; i++) {
      if (isContentLine(lines[i]!)) return lines[i]!.firstWordIndex!;
    }
  } else {
    for (let i = current - 1; i >= 0; i--) {
      if (isContentLine(lines[i]!)) return lines[i]!.firstWordIndex!;
    }
  }
  return position;
}
